import type {
  ContactThread,
  DeliveryStatus,
  MessageDelivery,
  TranscriptMessage,
} from '@/services/conversationService';

/**
 * Delivery state of the messages we sent over WhatsApp (delivery contract
 * 30.09.). Pure: no React, no I/O.
 *
 * A transcript row carries `wamids` (one reply can be several WhatsApp
 * messages) and, from the REST answer, the `delivery` aggregate: failed if one
 * failed, otherwise the lowest status. `whatsapp.message_status` events report
 * one wamid at a time — the functions here fold them into that aggregate.
 *
 * Rank: accepted < sent < delivered < read < failed. Status only ever moves
 * up (Meta's webhooks arrive out of order: a late `sent` after `read` changes
 * nothing), and `failed` beats everything — the same rule the server applies
 * when it stores the statuses.
 */

/** `whatsapp.message_status` payload. */
export interface WhatsAppMessageStatusEvent {
  contact_key: string;
  wamid: string;
  status: DeliveryStatus;
  error_code?: number | null;
  reason?: string | null;
}

/** What is known about one WhatsApp message. */
export interface WamidStatus {
  status: DeliveryStatus;
  error_code?: number | null;
  reason?: string | null;
}

export type WamidStatusMap = ReadonlyMap<string, WamidStatus>;

const RANK: Record<DeliveryStatus, number> = { accepted: 0, sent: 1, delivered: 2, read: 3, failed: 4 };

/** A wamid without a status row yet: Meta took it, nothing reported (contract). */
const ACCEPTED: MessageDelivery = Object.freeze({ status: 'accepted' });

/**
 * How long "Meta took it, nothing reported yet" is believed. Older than that
 * the status never came or was purged (the server keeps them 35 days): the
 * part is unknown and ignored — a clock that ticks forever would be a lie.
 * The server applies the same rule to the aggregate it sends.
 */
export const DELIVERY_ACCEPTED_MAX_AGE_MS = 10 * 60_000;

/** Written less than `DELIVERY_ACCEPTED_MAX_AGE_MS` ago; a row without a usable time is old. */
function isRecent(ts: string | null | undefined, now: number): boolean {
  const written = ts ? Date.parse(ts) : NaN;
  return !Number.isNaN(written) && now - written < DELIVERY_ACCEPTED_MAX_AGE_MS;
}

/** The status of one WhatsApp message as a row stores it: the aggregate, or "on its way" for a live row. */
function storedDelivery(message: TranscriptMessage): MessageDelivery | null {
  if (message.delivery) return message.delivery;
  return message.wamids?.length ? ACCEPTED : null;
}

/** The statuses an event can carry (`accepted` is our own "nothing reported yet"). */
function isReportedStatus(value: unknown): value is Exclude<DeliveryStatus, 'accepted'> {
  return value === 'sent' || value === 'delivered' || value === 'read' || value === 'failed';
}

/** The higher rank wins; on a tie the stored one stays (same reference). */
export function mergeWamidStatus(current: WamidStatus | undefined, incoming: WamidStatus): WamidStatus {
  return !current || RANK[incoming.status] > RANK[current.status] ? incoming : current;
}

/**
 * Delivery of a row as the bubble shows it. A row with wamids but without an
 * aggregate (it came live, not from the REST answer) counts as `accepted` —
 * for a few minutes only, see `DELIVERY_ACCEPTED_MAX_AGE_MS`; afterwards it
 * shows nothing. A row without wamids was not sent over WhatsApp by us: no
 * delivery at all. `now` is the server clock, so the clock flips off on time
 * even on a salon PC that runs fast or slow.
 */
export function deliveryOf(message: TranscriptMessage, now: number): MessageDelivery | null {
  const stored = storedDelivery(message);
  if (!stored) return null;
  return stored.status === 'accepted' && !isRecent(message.ts, now) ? null : stored;
}

/**
 * The row's aggregate after folding in what is known per wamid; the current
 * one (same reference) when that is not better. Never below what the row
 * already shows — the aggregate only grows, since every wamid only grows.
 * A wamid nobody reported on counts as what the row shows now (for "on its
 * way": only while the row is young; old, it is unknown and left out). When
 * no part is known at all there is nothing to fold in.
 */
function deliveryWithStatuses(message: TranscriptMessage, known: WamidStatusMap, now: number): MessageDelivery | null {
  const wamids = message.wamids;
  const base = storedDelivery(message);
  if (!wamids?.length || !base || base.status === 'failed') return base;
  const unreported: DeliveryStatus | null =
    base.status !== 'accepted' ? base.status : isRecent(message.ts, now) ? 'accepted' : null;
  let lowest: DeliveryStatus | null = null;
  for (const wamid of wamids) {
    const reported = known.get(wamid);
    if (reported?.status === 'failed') {
      return { status: 'failed', error_code: reported.error_code ?? null, reason: reported.reason ?? null };
    }
    const status = reported?.status ?? unreported;
    if (status !== null && (lowest === null || RANK[status] < RANK[lowest])) lowest = status;
  }
  return lowest !== null && RANK[lowest] > RANK[base.status] ? { status: lowest } : base;
}

/**
 * Fold the known per-wamid statuses into the thread's rows. The same thread
 * object when nothing changed, so a reconcile after every update costs no render.
 */
export function applyWamidStatuses(thread: ContactThread, known: WamidStatusMap, now: number): ContactThread {
  if (known.size === 0) return thread;
  let changed = false;
  const messages = thread.messages.map((message) => {
    if (!message.wamids?.length) return message;
    const next = deliveryWithStatuses(message, known, now);
    if (next === storedDelivery(message)) return message;
    changed = true;
    return { ...message, delivery: next };
  });
  return changed ? { ...thread, messages } : thread;
}

/** The lower of two aggregates (a failure beats everything); a missing one does not count. */
function lowerDelivery(
  a: MessageDelivery | null | undefined,
  b: MessageDelivery | null | undefined,
): MessageDelivery | null | undefined {
  if (!a) return b;
  if (!b) return a;
  if (a.status === 'failed') return a;
  if (b.status === 'failed') return b;
  return RANK[a.status] <= RANK[b.status] ? a : b;
}

/**
 * The same row seen twice (stream event and POST answer, in either order):
 * keep the row, add the wamids and what the other copy knows of delivery.
 * Same object when the other copy brings nothing.
 *
 * Same parts: the higher status wins (both are lower bounds of the truth).
 * New parts: the row is only as far as its slowest part, so the aggregate
 * cannot stay where the old parts were — it becomes the lower of both (a part
 * without an aggregate is on its way); a copy that covers every part we knew
 * is a newer snapshot of the whole row and simply replaces it. The known
 * statuses then lift it again (`applyWamidStatuses`).
 */
export function mergeDeliveryInfo(existing: TranscriptMessage, incoming: TranscriptMessage): TranscriptMessage {
  const knownWamids = existing.wamids ?? [];
  const added = (incoming.wamids ?? []).filter((wamid) => !knownWamids.includes(wamid));
  const wamids = added.length > 0 ? [...knownWamids, ...added] : existing.wamids;
  let delivery: MessageDelivery | null | undefined;
  if (added.length === 0) {
    delivery =
      incoming.delivery && (!existing.delivery || RANK[incoming.delivery.status] > RANK[existing.delivery.status])
        ? incoming.delivery
        : existing.delivery;
  } else if (incoming.delivery && knownWamids.every((wamid) => incoming.wamids?.includes(wamid))) {
    delivery = incoming.delivery;
  } else {
    delivery = lowerDelivery(existing.delivery, incoming.delivery ?? ACCEPTED);
  }
  if (wamids === existing.wamids && delivery === existing.delivery) return existing;
  return { ...existing, wamids, delivery };
}

/**
 * A status of one part of a multi-part message arrived while the others are
 * unknown to us: the REST aggregate is only a lower bound for them, so the
 * shown aggregate may lag behind. Worth one refetch — the server knows them all.
 */
export function needsSiblingResync(thread: ContactThread, wamid: string, known: WamidStatusMap): boolean {
  const message = thread.messages.find((m) => m.wamids?.includes(wamid));
  if (!message?.wamids || message.wamids.length < 2) return false;
  if (storedDelivery(message)?.status === 'failed') return false;
  return message.wamids.some((other) => other !== wamid && !known.has(other));
}

// --- wording -----------------------------------------------------------------------

const SAYS_UNDELIVERED = /^nicht zugestellt/i;

/** The server's German reason; without one, at least the error code. */
function failureReason(delivery: MessageDelivery): string | null {
  const reason = delivery.reason?.trim();
  if (reason) return reason;
  return delivery.error_code != null ? `Nicht zugestellt (Fehler ${delivery.error_code})` : null;
}

/** Visible line of a failed message: "Nicht zugestellt · Grund". */
export function failedText(delivery: MessageDelivery): string {
  const reason = failureReason(delivery);
  if (!reason) return 'Nicht zugestellt';
  // The server's fallback reason already starts with it: do not say it twice.
  return SAYS_UNDELIVERED.test(reason) ? reason : `Nicht zugestellt · ${reason}`;
}

/** Spoken name of the state (aria-label). */
export function deliveryLabel(delivery: MessageDelivery): string {
  switch (delivery.status) {
    case 'accepted':
      return 'Wird gesendet';
    case 'sent':
      return 'Gesendet';
    case 'delivered':
      return 'Zugestellt';
    case 'read':
      return 'Gelesen';
    case 'failed': {
      const reason = failureReason(delivery);
      if (!reason) return 'Nicht zugestellt';
      return SAYS_UNDELIVERED.test(reason) ? reason : `Nicht zugestellt: ${reason}`;
    }
  }
}

// --- buffer ------------------------------------------------------------------------

/**
 * How long a reported status is remembered. The race it covers (a status
 * before the row it belongs to, e.g. before the send answer is merged) lasts
 * seconds; a longer memory only serves the siblings of multi-part messages.
 */
export const STATUS_BUFFER_TTL_MS = 5 * 60_000;
export const STATUS_BUFFER_MAX_ENTRIES = 200;

export interface WamidStatusBuffer {
  readonly statuses: WamidStatusMap;
  /** Remember a reported status. False when it changed nothing (old, repeated, invalid). */
  record: (event: Pick<WhatsAppMessageStatusEvent, 'wamid' | 'status' | 'error_code' | 'reason'>) => boolean;
  clear: () => void;
}

export interface WamidStatusBufferOptions {
  ttlMs?: number;
  maxEntries?: number;
  now?: () => number;
}

/**
 * Per-wamid memory of the reported statuses — the thread applies it to every
 * state it builds, so a status that came first waits for its row. Bounded in
 * age and size.
 */
export function createWamidStatusBuffer({
  ttlMs = STATUS_BUFFER_TTL_MS,
  maxEntries = STATUS_BUFFER_MAX_ENTRIES,
  now = Date.now,
}: WamidStatusBufferOptions = {}): WamidStatusBuffer {
  const statuses = new Map<string, WamidStatus>();
  // Insertion order = order of the last change: the oldest entry is first.
  const seenAt = new Map<string, number>();

  const forget = (wamid: string) => {
    statuses.delete(wamid);
    seenAt.delete(wamid);
  };

  return {
    statuses,
    record: (event) => {
      if (!event.wamid || !isReportedStatus(event.status)) return false;
      const t = now();
      for (const [wamid, at] of seenAt) {
        if (t - at <= ttlMs) break;
        forget(wamid);
      }
      const incoming: WamidStatus = {
        status: event.status,
        ...(event.error_code != null ? { error_code: event.error_code } : {}),
        ...(event.reason ? { reason: event.reason } : {}),
      };
      const current = statuses.get(event.wamid);
      const merged = mergeWamidStatus(current, incoming);
      if (merged === current) return false;
      forget(event.wamid);
      statuses.set(event.wamid, merged);
      seenAt.set(event.wamid, t);
      while (statuses.size > maxEntries) {
        const oldest = statuses.keys().next().value;
        if (oldest === undefined) break;
        forget(oldest);
      }
      return true;
    },
    clear: () => {
      statuses.clear();
      seenAt.clear();
    },
  };
}
